import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import EmployeeDetailModal from './EmployeeDetailModal';
import { getRoleLabel, normalizeRole } from '../utils/roles';
import EmployeeManagementPanel from './EmployeeManagementPanel';
import { ROUTE_PATHS } from '../config/navigation';
import { formatAssignmentSummary } from '../utils/companyAssignments';
import useDebouncedValue from '../hooks/useDebouncedValue';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const getAvatarUrl = (employee) => {
  if (employee.avatar?.filePath) {
    return `${apiUrl}/${employee.avatar.filePath.replace(/\\/g, '/')}`;
  }

  return null;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('tr-TR');
};

const getRoleText = getRoleLabel;

const getRoleBadgeClass = (role) => {
  switch (normalizeRole(role)) {
    case 'super_admin':
      return 'modern-employee-badge-admin';
    case 'company_admin':
      return 'modern-employee-badge-manager';
    default:
      return 'modern-employee-badge-employee';
  }
};

const TumCalisanlarPage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 350);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [sendingSmsId, setSendingSmsId] = useState('');
  const hasLoadedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const { toasts, addToast, removeToast } = useToast();
  const isSuperAdmin = useMemo(() => {
    try {
      return normalizeRole(JSON.parse(localStorage.getItem('userData') || '{}').role) === 'super_admin';
    } catch (error) {
      return false;
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    api.getRoles().then((response) => setRoles(response.data || [])).catch(() => setRoles([]));
    if (isSuperAdmin) {
      api.getCompanies().then((response) => setCompanies(response.companies || [])).catch(() => setCompanies([]));
    }
  }, [isSuperAdmin]);

  const fetchEmployees = useCallback(async () => {
    const requestId = ++requestSequenceRef.current;
    try {
      if (hasLoadedRef.current) setRefreshing(true);
      else setLoading(true);
      const response = await api.getUsers({
        page: currentPage,
        limit: 12,
        search: debouncedSearchTerm,
        department: departmentFilter,
        role: roleFilter,
        status: statusFilter,
        ...(isSuperAdmin
          ? (companyFilter ? { companyId: companyFilter } : { allCompanies: '1' })
          : {})
      });

      if (requestId === requestSequenceRef.current && response.success) {
        setEmployees(response.users || []);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      if (requestId !== requestSequenceRef.current) return;
      console.error('Employees fetch error:', error);
      addToast('Çalışan listesi alınamadı.', 'error', 4000);
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [addToast, companyFilter, currentPage, debouncedSearchTerm, departmentFilter, isSuperAdmin, roleFilter, statusFilter]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSendLoginSms = async (employee) => {
    if (!employee.phone) {
      addToast('Bu çalışanın kayıtlı telefon numarası yok.', 'error', 4000);
      return;
    }
    if (!window.confirm(`${employee.firstName} ${employee.lastName} için yeni şifre oluşturulup SMS ile gönderilsin mi?`)) {
      return;
    }
    try {
      setSendingSmsId(employee._id);
      const response = await api.sendLoginSms(employee._id, isSuperAdmin);
      addToast(response.message || 'Giriş bilgileri SMS ile gönderildi.', 'success', 4000);
    } catch (error) {
      addToast(error.message || 'SMS gönderilemedi.', 'error', 4000);
    } finally {
      setSendingSmsId('');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.getDepartments();
      if (response.success) {
        setDepartments(response.departments || []);
      }
    } catch (error) {
      console.error('Departments fetch error:', error);
    }
  };

  const summary = useMemo(
    () => ({
      active: employees.filter((employee) => employee.isActive).length,
      visible: employees.filter((employee) => employee.visibility?.directory !== false).length
    }),
    [employees]
  );

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>Çalışan Yönetimi</h1>
              <p>Çalışan bilgilerini, erişim rollerini ve hesap durumlarını yönetin.</p>
            </div>
            <div className="employee-management-header-side">
              <div className="employee-management-header-actions">
                <Link to={ROUTE_PATHS.userRegistration} className="employee-management-add-button">
                  <i className="bx bx-user-plus" aria-hidden="true" />
                  Çalışan ekle / Excel aktar
                </Link>
              </div>
              <div className="directory-admin-summary">
                <span>{employees.length} listelenen kayıt</span>
                <span>{summary.active} aktif çalışan</span>
                <span>{summary.visible} rehber görünürlüğü açık</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-filter-alt"></i>
              <h4>Filtreler</h4>
            </div>
          </div>
          <div className="modern-hr-filters">
            <div className="row">
              <div className="col-md-4">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Arama</label>
                  <div className="modern-hr-search-input">
                    <i className="bx bx-search"></i>
                    <input
                      className="modern-hr-form-input"
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Ad, e-posta, unvan veya departman"
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Departman</label>
                  <select
                    className="modern-hr-form-select"
                    value={departmentFilter}
                    onChange={(event) => {
                      setDepartmentFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tüm departmanlar</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Rol</label>
                  <select
                    className="modern-hr-form-select"
                    value={roleFilter}
                    onChange={(event) => {
                      setRoleFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tüm roller</option>
                    <option value="employee">Çalışan</option>
                    <option value="company_admin">Admin</option>
                    <option value="super_admin">Süper Admin</option>
                  </select>
                </div>
              </div>
              {isSuperAdmin && (
                <div className="col-md-3">
                  <div className="modern-hr-form-group">
                    <label className="modern-hr-form-label">Firma</label>
                    <select
                      className="modern-hr-form-select"
                      value={companyFilter}
                      onChange={(event) => {
                        setCompanyFilter(event.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Tüm firmalar</option>
                      {companies.map((company) => (
                        <option key={company._id} value={company._id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="col-md-2">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Durum</label>
                  <select
                    className="modern-hr-form-select"
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget search-results-region" aria-busy={refreshing}>
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-group"></i>
              <h4>Çalışan listesi</h4>
            </div>
          </div>

          {refreshing && (
            <div className="search-refresh-indicator" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Sonuçlar güncelleniyor…
            </div>
          )}

          {loading ? (
            <div className="modern-hr-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              <p>Çalışanlar yükleniyor...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="modern-hr-empty">
              <i className="bx bx-user-x"></i>
              <h5>Çalışan bulunamadı</h5>
              <p>Seçtiğiniz filtrelerle eşleşen kayıt yok.</p>
            </div>
          ) : (
            <>
              <div className="modern-employee-list">
                {employees.map((employee) => {
                  const avatarUrl = getAvatarUrl(employee);

                  return (
                    <div
                      key={employee._id}
                      type="button"
                      className="modern-employee-list-item employee-list-button"
                    >
                      <div className="modern-employee-list-avatar">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                        ) : (
                          <div className="modern-employee-avatar-placeholder">
                            {employee.firstName?.charAt(0)}
                            {employee.lastName?.charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className="modern-employee-list-content">
                        <div className="modern-employee-list-main">
                          <h5 className="modern-employee-name">
                            {employee.firstName} {employee.lastName}
                          </h5>
                          <p className="modern-employee-email">{employee.email}</p>
                          <p className="modern-employee-position">{employee.position}</p>
                          <p className="modern-employee-department">
                            {formatAssignmentSummary(employee) || [employee.company, employee.department].filter(Boolean).join(' • ')}
                          </p>
                        </div>

                        <div className="modern-employee-list-info">
                          <div className="modern-employee-info-item">
                            <i className="bx bx-calendar"></i>
                            <span>{formatDate(employee.startDate)}</span>
                          </div>
                          <div className="modern-employee-info-item">
                            <i className="bx bx-map"></i>
                            <span>{[
                              employee.officeLocation,
                              employee.officeBuilding && `Bina ${employee.officeBuilding}`,
                              employee.officeFloor && `Kat ${employee.officeFloor}`
                            ].filter(Boolean).join(' • ') || '-'}</span>
                          </div>
                          {employee.extension && (
                            <div className="modern-employee-info-item">
                              <i className="bx bx-phone-call"></i>
                              <span>Dahili {employee.extension}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="modern-employee-list-badges">
                        <span className={`modern-employee-badge ${getRoleBadgeClass(employee.role)}`}>
                          {getRoleText(employee.role)}
                        </span>
                        <span
                          className={`modern-employee-badge ${
                            employee.isActive
                              ? 'modern-employee-badge-active'
                              : 'modern-employee-badge-inactive'
                          }`}
                        >
                          {employee.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        <span className="modern-employee-badge modern-employee-badge-employee">
                          {employee.visibility?.directory === false ? 'Rehber kapalı' : 'Rehber açık'}
                        </span>
                        <div className="employee-row-actions">
                          <button type="button" onClick={() => setSelectedEmployee(employee)}>Görüntüle</button>
                          <button type="button" className="primary" onClick={() => setEditingEmployee(employee)}>Düzenle</button>
                          <button type="button" onClick={() => handleSendLoginSms(employee)} disabled={sendingSmsId === employee._id}>
                            {sendingSmsId === employee._id ? 'Gönderiliyor…' : 'SMS ile gönder'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="modern-hr-pagination">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="modern-hr-btn modern-hr-btn-secondary"
                  >
                    Önceki
                  </button>
                  <div className="modern-hr-pagination-info">
                    Sayfa {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="modern-hr-btn modern-hr-btn-secondary"
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EmployeeDetailModal
        employee={selectedEmployee}
        isOpen={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
      />
      <EmployeeManagementPanel
        employee={editingEmployee}
        roles={roles}
        onClose={() => setEditingEmployee(null)}
        onMessage={(message, type) => addToast(message, type, 4000)}
        onSaved={(updated) => {
          setEmployees((current) => updated._deleted ? current.filter((item) => item._id !== updated._id) : current.map((item) => item._id === updated._id ? updated : item));
          if (!updated._deleted) setEditingEmployee(updated);
        }}
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TumCalisanlarPage;
